import { getCurrentUser } from '../auth/session.ts';
import { get, put, _delete } from '../utils/http.ts';
import type { PuzzleId } from '../components/luna-common';
import type { Options } from './wasmBridge/viewModel';

export interface GameStateSummary {
  puzzle: PuzzleId;
  level: number | null;
  options: Options[] | null;
  label: string;
  updatedAt: string;
}

export interface GameStateRow extends GameStateSummary {
  state: string;
  checkpoint: string | null;
}

export interface GameStateSave {
  level: number | null;
  options: Options[] | null;
  label: string;
  state: string;
  checkpoint: string | null;
}

export async function loadGameStates(): Promise<GameStateSummary[] | null> {
  const email = getCurrentUser().emails[0].email;
  const response = await get<{ gameStates: GameStateSummary[] }>(
    `participant/${encodeURIComponent(email)}/game-states`,
  );

  if (response.error || !response.result) {
    console.error('Error loading game states:', response.error);
    return null;
  }

  return response.result.gameStates ?? [];
}

export async function loadGameState(puzzle: PuzzleId): Promise<GameStateRow | null> {
  const email = getCurrentUser().emails[0].email;
  const response = await get<{ gameState: GameStateRow | null }>(
    `participant/${encodeURIComponent(email)}/game-state/${encodeURIComponent(puzzle)}`,
  );

  if (response.error || !response.result) {
    console.error('Error loading game state:', response.error);
    return null;
  }

  return response.result.gameState ?? null;
}

export async function saveGameState(puzzle: PuzzleId, payload: GameStateSave): Promise<boolean> {
  const email = getCurrentUser().emails[0].email;
  const url = `participant/${encodeURIComponent(email)}/game-state/${encodeURIComponent(puzzle)}`;
  const response = await put<GameStateSave, { gameState: unknown }>(url, payload, {
    keepalive: true,
  });

  if (response.error || !response.result) {
    console.error('Error saving game state:', response.error);
    return false;
  }

  return true;
}

export async function clearGameState(puzzle: PuzzleId): Promise<boolean> {
  const email = getCurrentUser().emails[0].email;
  const response = await _delete(
    `participant/${encodeURIComponent(email)}/game-state/${encodeURIComponent(puzzle)}`,
  );

  if (response.error) {
    console.error('Error clearing game state:', response.error);
    return false;
  }

  return true;
}
